import { MouseEvent, useMemo } from 'react';

import { useTheme } from '@mui/material';
import dayjs from 'dayjs';

import { COPILOT } from '@cast/constants';
import { Box, Button, Icons, Tooltip, tokens } from '@cast/design-system';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import {
  isRecommendedRequestsChangedEvent,
  PreparedEvent,
} from '../../../types';

type ExplainRecommendationCellProps = {
  event: PreparedEvent;
};

/**
 * Row action that opens OpsPilot with a prompt asking to explain the
 * recommendation of a "Recommendation generated" event. Renders nothing for
 * other event types or when OpsPilot is not enabled.
 */
export const ExplainRecommendationCell = ({
  event,
}: ExplainRecommendationCellProps) => {
  const theme = useTheme();
  const { cluster } = useCluster();
  const isRecommendationEvent = isRecommendedRequestsChangedEvent(event);

  // A recommendation event belongs to a single workload.
  const workload = event.workloads?.[0];
  const workloadLabel = workload
    ? `${workload.kind} ${workload.namespace}/${workload.name}`
    : undefined;

  // Same shape as the other OpsPilot prompts: cluster as the scope, a short
  // noun phrase as the context and a time range. The prompt carries no event
  // id, so the range is a small window around the event for the agent to find
  // it by time. The WOOP agent's keyword router sends generate/create/
  // optimize/configure/build wording to the create-policies flow, so keep
  // those words out.
  const buildPrompt = useMemo(
    () =>
      buildAnalyzePrompt({
        scopeName: cluster.name,
        context: workloadLabel
          ? `WOOP recommendation for workload ${workloadLabel}`
          : 'WOOP recommendation',
        getDefaultRange: () => ({
          from: dayjs(event.createdAt).subtract(1, 'minute').toISOString(),
          to: dayjs(event.createdAt).add(1, 'minute').toISOString(),
        }),
      }),
    [event.createdAt, cluster.name, workloadLabel]
  );

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: isRecommendationEvent ? buildPrompt : undefined,
  });

  if (!analyzeWithOpsPilot) {
    return null;
  }

  const { getPrompt, onClick } = analyzeWithOpsPilot;

  const handleClick = (e: MouseEvent) => {
    // The row toggles its expanded panel on click.
    e.stopPropagation();
    const prompt = getPrompt();
    if (prompt) {
      onClick(prompt);
    }
  };

  return (
    <Tooltip title="Explain this recommendation with OpsPilot" placement="left">
      {/* Same look as the OpsPilot button in the header. */}
      <Box
        sx={{
          borderRadius: '6px',
          outline: `1px solid ${tokens.theme.border.structural.neutral}`,
          outlineOffset: '-1px',
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClick}
          startIcon={
            <Icons.CastCopilot
              size={16}
              color={tokens.theme.fg.action.primary}
            />
          }
          aria-label="Explain this recommendation with OpsPilot"
          testId="event-explain-with-opspilot"
          sx={{ border: 'none', ...theme.typography.P14M }}
        >
          {COPILOT}
        </Button>
      </Box>
    </Tooltip>
  );
};
